import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';
import { RouterModule, Routes } from '@angular/router';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ModalModule } from 'ngx-bootstrap/modal';
import { NgxGalleryModule } from 'ngx-gallery-14';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { NgxStarsModule } from 'ngx-stars';
import { AuthGuard } from '../shared/auth.guard';
import { CanDeactivateGuard } from '../shared/can.deactivate.guard';
import { UserTypes } from '../shared/interfaces';
import { SharedModule } from '../shared/shared.module';
import { InspectionAddDateComponent } from './add.date/add.date.component';
import { AddFlagComponent } from './add.flag/add.flag.component';
import { AddGroupComponent } from './add.group/add.group.component';
import { AnswerFormComponent } from './answer-form/answer-form.component';
import { AnswerInvalidFormComponent } from './answer-invalid-form/answer-invalid-form.component';
import { AnswerPicturesComponent } from './answer-pictures/answer-pictures.component';
import { InspectionAssignInspectorComponent } from './assign.inspector/assign.inspector.component';
import { InspectionAssignVendorComponent } from './assign.vendor/assign.vendor.component';
import { BoxPickerComponent } from './box-picker/box.picker.component';
import { BoxViewerComponent } from './box-viewer/box.viewer.component';
import { CreateVersionModalComponent } from './create-version-modal/create-version-modal.component';
import { EditFlagDetailsComponent } from './edit.flag.details/edit.flag.details.component';
import { EditFlagComponent } from './edit.flag/edit.flag.component';
import { EditGroupComponent } from './edit.group/edit.group.component';
import { ExtensionGrantedDeadlinesComponent } from './extension-granted-deadlines/extension-granted-deadlines.component';
import { FlagFlowComponent } from './flag-flow/flag-flow.component';
import { FilterPipe } from './flag-group-template/filter.pipe';
import { FlagGroupTemplateComponent } from './flag-group-template/flag-group-template.component';
import { StripHtmlPipe } from './flag-group-template/strip-html.pipe';
import { FlagManagerComponent } from './flag-manager/flag-manager.component';
import { GroupPickerComponent } from './group-picker/group-picker.component';
import { InspectionAcceptedComponent } from './inspection-accepted/inspection-accepted.component';
import { InspectionArchivedComponent } from './inspection-archived/inspection-archived.component';
import { InspectionAssigneeFilter } from './inspection-assignee-filter/inspection-assignee-filter.component';
import { InspectionChangeComponent } from './inspection-change/inspection-change.component';
import { InspectionCommentComponent } from './inspection-comment/inspection-comment.component';
import { InspectionCompletedComponent } from './inspection-completed/inspection-completed.component';
import { InspectionFilesPickerComponent } from './inspection-files-picker/inspection-files-picker.component';
import { InspectionFilesComponent } from './inspection-files/inspection-files.component';
import { InspectionFlowComponent } from './inspection-flow/inspection-flow.component';
import { SearchPipe } from './inspection-flow/search.pipe';
import { InspectionFormComponent } from './inspection-form/inspection-form.component';
import { InspectionFullReviewComponent } from './inspection-full-review/inspection-full-review.component';
import { InspectionGalleryComponent } from './inspection-gallery/inspection-gallery.component';
import { InspectionLocationsComponent } from './inspection-locations/inspection-locations.component';
import { InspectionNotesComponent } from './inspection-notes/inspection-notes.component';
import { InspectionPopUpComponent } from './inspection-popup/inspection-popup.component';
import { InspectionProductFilter } from './inspection-product-filter/inspection-product-filter.component';
import { InspectionRejectedComponent } from './inspection-rejected/inspection-rejected.component';
import { InspectionReviewInvalidComponent } from './inspection-review-invalid/inspection-review-invalid.component';
import { InspectionReviewComponent } from './inspection-review/inspection-review.component';
import { InspectionSearchResultsComponent } from './inspection-search-results/inspection-search-results.component';
import { InspectionSearchComponent } from './inspection-search/inspection-search.component';
import { HighlightSearchPipe } from './inspection-search/lighlight-search.pipe';
import { InspectionSnapshotComponent } from './inspection-snapshot/inspection-snapshot.component';
import { InspectionVendorFlowComponent } from './inspection-vendor-flow/inspection-vendor-flow.component';
import { InspectionVendorFormComponent } from './inspection-vendor-form/inspection-vendor-form.component';
import { InspectionVendorOverviewComponent } from './inspection-vendor-overview/inspection-vendor-overview.component';
import { InspectionWaitingComponent } from './inspection-waiting/inspection-waiting.component';
import { InspectionRootRedirect } from './inspection.root.redirect';
import { InspectionResolver } from './inspector.resolver';
import { PictureViewerComponent } from './picture-viewer/picture-viewer.component';
import { RateInspectionComponent } from './rate-inspection/rate-inspection.component';
import { ReviewNavigationComponent } from './review.navigation/review.navigation.component';
import { InspectionSendEmailComponent } from './send.email/send.email.component';
import { InspectionSetDeadlinesComponent } from './set.violation.deadlines/set.violation.deadlines.component';
import { UnpinnedFlagsComponent } from './unpinned-flags/unpinned-flags.component';
import { UploadImageComponent } from './upload.image/upload.image.component';

const routes: Routes = [
  {
    path: '',
    data: { title: 'Inspection' },
    children: [
      { path: '', redirectTo: 'flow', pathMatch: 'full' },
      {
        path: 'form/:id',
        canActivate: [AuthGuard],
        component: InspectionFormComponent,
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Details', userTypes: [UserTypes.ADMIN] },
        resolve: { inspection: InspectionResolver },
      },
      {
        path: 'search',
        canActivate: [InspectionRootRedirect, AuthGuard],
        component: InspectionSearchResultsComponent,
        data: { title: 'Search', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'flow',
        canActivate: [InspectionRootRedirect, AuthGuard],
        component: InspectionFlowComponent,
        data: { title: 'Flow', userTypes: [UserTypes.ADMIN, UserTypes.VENDOR] },
      },
      {
        path: 'underwriting',
        canActivate: [AuthGuard],
        component: FlagFlowComponent,
        data: { title: 'Flow', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'accepted',
        canActivate: [AuthGuard],
        component: InspectionAcceptedComponent,
        data: { title: 'Accepted', userTypes: [UserTypes.ADMIN, UserTypes.VENDOR] },
      },
      {
        path: 'archived',
        canActivate: [AuthGuard],
        component: InspectionArchivedComponent,
        data: { title: 'Archived', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'completed',
        canActivate: [AuthGuard],
        component: InspectionCompletedComponent,
        data: { title: 'Completed', userTypes: [UserTypes.ADMIN, UserTypes.VENDOR] },
      },
      {
        path: 'rejected',
        canActivate: [AuthGuard],
        component: InspectionRejectedComponent,
        data: { title: 'Rejected', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'waiting',
        canActivate: [AuthGuard],
        component: InspectionWaitingComponent,
        data: { title: 'Waiting', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'review-invalid/:id',
        canActivate: [AuthGuard],
        component: InspectionReviewInvalidComponent,
        data: { title: 'Review Invalid', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'review/:view/:id',
        canActivate: [AuthGuard],
        component: InspectionFullReviewComponent,
        data: { title: 'Review', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'vendor/overview/:id',
        canActivate: [AuthGuard],
        component: InspectionVendorOverviewComponent,
        data: { title: 'Inspection Overview', userTypes: [UserTypes.ADMIN] },
      },
      {
        path: 'vendor/flow',
        canActivate: [AuthGuard],
        component: InspectionVendorFlowComponent,
        data: { title: 'Flow', userTypes: [UserTypes.VENDOR] },
      },
      {
        path: 'vendor/form/:id',
        canActivate: [AuthGuard],
        component: InspectionVendorFormComponent,
        data: { title: 'Details', userTypes: [UserTypes.VENDOR] },
        resolve: { inspection: InspectionResolver },
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    SharedModule,
    RouterModule.forChild(routes),
    ModalModule.forRoot(),
    AccordionModule.forRoot(),
    PerfectScrollbarModule,
    NgxGalleryModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    NgxStarsModule,
  ],
  declarations: [
    InspectionFlowComponent,
    InspectionAcceptedComponent,
    InspectionArchivedComponent,
    InspectionCompletedComponent,
    InspectionRejectedComponent,
    InspectionWaitingComponent,
    InspectionFormComponent,
    InspectionVendorFlowComponent,
    InspectionAssignVendorComponent,
    InspectionSendEmailComponent,
    InspectionAssignInspectorComponent,
    InspectionAddDateComponent,
    InspectionReviewComponent,
    InspectionReviewInvalidComponent,
    InspectionSetDeadlinesComponent,
    AnswerFormComponent,
    AnswerInvalidFormComponent,
    AnswerPicturesComponent,
    InspectionSnapshotComponent,
    InspectionVendorFormComponent,
    InspectionVendorOverviewComponent,
    RateInspectionComponent,
    InspectionSearchComponent,
    SearchPipe,
    HighlightSearchPipe,
    InspectionGalleryComponent,
    InspectionCommentComponent,
    AddFlagComponent,
    EditFlagComponent,
    AddGroupComponent,
    EditGroupComponent,
    EditFlagDetailsComponent,
    ReviewNavigationComponent,
    InspectionFullReviewComponent,
    PictureViewerComponent,
    InspectionPopUpComponent,
    FlagManagerComponent,
    GroupPickerComponent,
    FlagFlowComponent,
    UnpinnedFlagsComponent,
    InspectionSearchResultsComponent,
    InspectionChangeComponent,
    InspectionNotesComponent,
    InspectionLocationsComponent,
    BoxViewerComponent,
    BoxPickerComponent,
    InspectionFilesComponent,
    InspectionFilesPickerComponent,
    UploadImageComponent,
    CreateVersionModalComponent,
    FlagGroupTemplateComponent,
    InspectionAssigneeFilter,
    FilterPipe,
    StripHtmlPipe,
    ExtensionGrantedDeadlinesComponent,
    InspectionProductFilter,
  ],
  exports: [
    RouterModule,
    InspectionSearchComponent,
    PictureViewerComponent,
    UnpinnedFlagsComponent,
    InspectionGalleryComponent,
    AddFlagComponent,
    EditFlagComponent,
    UploadImageComponent,
    ExtensionGrantedDeadlinesComponent,
    SearchPipe,
  ],
})
export class InspectionModule {}
